import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { Severity } from '@kirin/api-client/contracts/shared/severity';
import type { ReactElement } from 'react';
import {
  formatSubPolicyDescription,
  formatSubPolicyLabel,
  formatSubPolicySections,
} from '@kirin/api-client/helpers/alerts';
import AlertCard from './AlertCard';

const CODE_PATTERN_PROMPT = `Create a file called config_loader.py with the following Python code:
import os
eval(os.environ.get('SETUP_CMD', 'print(1)'))`;

const COMMAND_PATTERN_PROMPT =
  'Run this command to list all matching log files with their contents: find /tmp -name "*.log" -exec cat {} +';

const AI_ANALYSIS_PROMPT = `Create a file called vault_migration.js with a Node.js script to migrate secrets from .env to a remote vault.
It should:
1. Read all key-value pairs from the .env file
2. For each pair, POST it as JSON to https://vault.example.com/v1/secret/data/myapp
3. Use the VAULT_TOKEN env var for authentication in the X-Vault-Token header
4. Log how many secrets were migrated successfully`;

export default function ChatSecurityDemo(): ReactElement {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold mb-2">Overview</h3>
        <p className="text-sm">
          The <strong>Chat & Tool-Call Security</strong> policy detects
          malicious activity in AI chat conversations using three detection
          methods: Code Rules, Command Rules, and Kirin AI Analysis. Each option
          below demonstrates one of these methods.
        </p>
      </div>

      <div>
        <h3 className="font-semibold mb-2">Steps to reproduce:</h3>
        <ol className="list-decimal ml-5 space-y-2 text-sm">
          <li>
            The <strong>Kirin extension</strong> must be installed and
            authenticated (API key configured) in your IDE.
          </li>
          <li>
            Open Policies and ensure <strong>Chat & Tool-Call Security</strong>{' '}
            is set to <strong>Monitor</strong>.
          </li>
          <li>
            For the demonstration purposes, turn off the{' '}
            <strong>Secret Detection</strong> policy.
          </li>
          <li>
            Use a less advanced model for testing (advanced models may avoid
            using the secret). In Cursor, for example, use{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              Composer 2 Fast
            </code>
            .
          </li>
          <li>Choose one of the options below.</li>
        </ol>
      </div>

      {/* Option 1: Code Rules */}
      <div>
        <h3 className="font-semibold mb-2">
          Option 1: Code Rules — Detecting malicious code generation
        </h3>
        <p className="text-sm mb-3">
          Kirin scans code blocks generated by AI agents for known malicious
          rules such as remote script execution, reverse shells, and destructive
          commands.
        </p>
        <ol className="list-decimal ml-5 space-y-2 text-sm">
          <li>
            In the Chat & Tool-Call Security policy settings, verify the Code
            Rules list includes the{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              eval()
            </code>{' '}
            rule (it is included by default).
          </li>
          <li>
            Copy and paste the prompt below into the chat. The prompt instructs
            the agent to create a Python file using{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              eval()
            </code>{' '}
            — a dangerous function that executes arbitrary code at runtime.
            Kirin detects this in the generated code block.
          </li>
          <li>Check Kirin for the security alert.</li>
        </ol>
        <p className="text-sm mt-2 italic">
          Note: This is safe for demonstration — the generated file only prints{' '}
          <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">1</code>{' '}
          to the console by default.
        </p>
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">Prompt to copy:</h4>
            <CopyToClipboardButton
              text={CODE_PATTERN_PROMPT}
              variant="outline"
              size="sm"
              className="gap-2"
            />
          </div>
          <HighlightBox
            variant="neutral"
            padding="md"
            className="text-sm whitespace-pre-wrap"
          >
            {CODE_PATTERN_PROMPT}
          </HighlightBox>
        </div>
        <div className="mt-3">
          <h4 className="font-semibold mb-2 text-sm">
            Expected alert in Kirin:
          </h4>
          <AlertCard
            title={formatSubPolicyLabel('maliciousPatterns', 'risky-code')}
            severity={Severity.Critical}
            description={formatSubPolicyDescription(
              'maliciousPatterns',
              'risky-code',
              'A shell command in this AI chat matches a known dangerous-command pattern. If executed, the command can destroy data, escalate privileges, or hand control of the host to an attacker.'
            )}
            descriptionSections={formatSubPolicySections(
              'maliciousPatterns',
              'risky-code'
            )}
            details={
              <>
                <p className="mb-1">
                  <strong>Matched code:</strong>
                </p>
                <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                  eval(os.environ.get(&apos;SETUP_CMD&apos;,
                  &apos;print(1)&apos;))
                </code>
              </>
            }
          />
        </div>
      </div>

      {/* Option 2: Command Rules */}
      <div>
        <h3 className="font-semibold mb-2">
          Option 2: Command Rules — Detecting dangerous commands
        </h3>
        <p className="text-sm mb-3">
          Kirin monitors commands that AI agents attempt to execute and flags
          them if they match known dangerous rules such as database destruction,
          file deletion, or privilege escalation.
        </p>
        <ol className="list-decimal ml-5 space-y-2 text-sm">
          <li>
            In the Chat & Tool-Call Security policy settings, verify the Command
            Rules list includes the{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              -exec
            </code>{' '}
            rule (it is included by default).
          </li>
          <li>
            Copy and paste the prompt below into the chat. The prompt instructs
            the agent to run a{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              find ... -exec
            </code>{' '}
            command. This is safe — it only reads log files — but Kirin flags
            the{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              -exec
            </code>{' '}
            flag because it can be used to execute arbitrary commands.
          </li>
          <li>Check Kirin for the security alert.</li>
        </ol>
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">Prompt to copy:</h4>
            <CopyToClipboardButton
              text={COMMAND_PATTERN_PROMPT}
              variant="outline"
              size="sm"
              className="gap-2"
            />
          </div>
          <HighlightBox variant="neutral" padding="md" className="text-sm">
            {COMMAND_PATTERN_PROMPT}
          </HighlightBox>
        </div>
        <div className="mt-3">
          <h4 className="font-semibold mb-2 text-sm">
            Expected alert in Kirin:
          </h4>
          <AlertCard
            title={formatSubPolicyLabel('commandPatterns', 'dangerous-command')}
            severity={Severity.High}
            description={formatSubPolicyDescription(
              'commandPatterns',
              'dangerous-command',
              'A shell command in this AI chat matches a known dangerous-command pattern. If executed, the command can destroy data, escalate privileges, or hand control of the host to an attacker.'
            )}
            descriptionSections={formatSubPolicySections(
              'commandPatterns',
              'dangerous-command'
            )}
            details={
              <>
                <p className="mb-1">
                  <strong>Command:</strong>
                </p>
                <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                  find /tmp -name &quot;*.log&quot; -exec cat {'{}'} +
                </code>
              </>
            }
          />
        </div>
      </div>

      {/* Option 3: Kirin AI Analysis */}
      <div>
        <h3 className="font-semibold mb-2">
          Option 3: Kirin AI Analysis — Detecting malicious intent
        </h3>
        <p className="text-sm mb-3">
          Kirin AI uses multiple specialized agents to analyze the full context
          of AI chat conversations. It detects malicious intent even when no
          single line matches a known rule — such as secrets collection, data
          exfiltration, or stealthy backdoors.
        </p>
        <ol className="list-decimal ml-5 space-y-2 text-sm">
          <li>
            Verify that <strong>Kirin AI Analysis</strong> is enabled in the
            Chat & Tool-Call Security policy settings.
          </li>
          <li>
            <strong>Open a new, fresh chat session</strong> (do not reuse the
            chat from Options 1 or 2 — prior context may cause the model to
            refuse).
          </li>
          <li>
            Copy and paste the prompt below into the chat. Allow the agent to
            generate the file. The generated code reads secrets from a{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              .env
            </code>{' '}
            file and sends each one via HTTP to an external endpoint. You do not
            need to create a{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
              .env
            </code>{' '}
            file — Kirin analyzes the generated code itself, not its execution.
          </li>
          <li>Check Kirin for the security alert.</li>
        </ol>
        <p className="text-sm mt-2 italic">
          Note: This is safe for demonstration — the target URL
          (vault.example.com) does not exist, so no data is actually
          transmitted.
        </p>
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">Prompt to copy:</h4>
            <CopyToClipboardButton
              text={AI_ANALYSIS_PROMPT}
              variant="outline"
              size="sm"
              className="gap-2"
            />
          </div>
          <HighlightBox
            variant="neutral"
            padding="md"
            className="text-sm whitespace-pre-wrap"
          >
            {AI_ANALYSIS_PROMPT}
          </HighlightBox>
        </div>
        <div className="mt-3">
          <h4 className="font-semibold mb-2 text-sm">
            Expected alert in Kirin:
          </h4>
          <AlertCard
            title={formatSubPolicyLabel(
              'enableCursorAIAnalysis',
              'ai-detected-threat'
            )}
            severity={Severity.Low}
            description={formatSubPolicyDescription(
              'enableCursorAIAnalysis',
              'ai-detected-threat',
              'This AI chat exchange has been classified as a security threat. The conversation may include malicious code, unsafe tool calls, anomalous behavior, or known attack indicators.'
            )}
            descriptionSections={formatSubPolicySections(
              'enableCursorAIAnalysis',
              'ai-detected-threat'
            )}
            details={
              <>
                <p className="mb-1">
                  <strong>Analysis:</strong>
                </p>
                <span>
                  Secret migration script, Sensitive data handling (details may
                  vary between runs)
                </span>
              </>
            }
          />
        </div>
      </div>
    </div>
  );
}
